/**
 * Helper central de chamadas à Edge Function `api`. NENHUMA fonte do
 * client deve montar a URL de proxy manualmente (`fetch('/api/...')`)
 * nem hardcodar o header `apikey` — todas passam por `apiFetch()`.
 *
 * PROBLEMA QUE ISSO RESOLVE: no Firebase, o rewrite do Hosting fazia
 * `/api/**` apontar pra Cloud Function, então `fetch('/api/firms')`
 * relativo funcionava direto. Supabase Edge Functions não tem
 * equivalente — toda invocação é uma URL absoluta com o formato
 * `https://<PROJECT_REF>.supabase.co/functions/v1/<nome-da-function>/<rota>`
 * (confirmado: supabase.com/docs/guides/functions, exemplos oficiais).
 * Como nossa function se chama `api`, o path physical já nasce sob
 * `/functions/v1/api/*` — não é um prefixo que escolhemos, é como o
 * Supabase resolve roteamento de Edge Functions por design.
 *
 * VITE_SUPABASE_FUNCTIONS_URL é OPCIONAL: se não for configurada, a
 * base URL de functions é derivada automaticamente de
 * VITE_SUPABASE_URL (padrão `<url>/functions/v1/api`, que é a
 * configuração default de qualquer projeto Supabase sem custom
 * domain). Configure a variável explicitamente só se precisar
 * apontar pra um domínio diferente do padrão.
 *
 * ACHADO ADICIONAL (não previsto na spec original desta etapa, mas
 * estruturalmente necessário pra qualquer chamada funcionar): toda
 * invocação de Edge Function exige o header `apikey` — é o gateway da
 * Supabase autorizando acesso ao projeto, uma camada anterior e
 * independente de qualquer autenticação de usuário (confirmado:
 * supabase.com/docs/guides/functions/auth-headers, "A separate apikey
 * header is required to access your project's APIs"). Sem esse
 * header, toda chamada seria rejeitada pelo gateway antes mesmo de
 * chegar no roteador Hono — por isso ele é injetado aqui, uma vez, em
 * vez de cada fonte precisar lembrar de adicioná-lo.
 *
 * As 10 rotas HTTP desta function (`api`) exigem `verify_jwt = false`
 * no supabase/config.toml (ver arquivo, seção [functions.api]) — sem
 * isso, o gateway exigiria também um JWT de usuário autenticado no
 * header Authorization, que nenhuma dessas rotas precisa (são
 * públicas, sem login). Isso não enfraquece a segurança das rotas
 * `/cron/*`: elas continuam exigindo Bearer da service role key,
 * checado pelo próprio middleware Hono dentro do código da function,
 * independente do que o gateway decide sobre verify_jwt.
 */

function requireEnv(name: string): string {
  const value = import.meta.env[name] as string | undefined;
  if (!value) {
    throw new Error(
      `Variável de ambiente "${name}" ausente. Configure-a no ambiente do Cloudflare Pages ` +
        '(ou em app/.env.local para desenvolvimento).',
    );
  }
  return value;
}

/**
 * Resolve a base URL das Edge Functions. Se VITE_SUPABASE_FUNCTIONS_URL
 * for explicitamente configurada, usa ela (permite customização futura,
 * ex: um custom domain só pra functions). Senão, deriva automaticamente
 * de VITE_SUPABASE_URL — reduz o risco de alguém atualizar uma variável
 * e esquecer da outra, já que o padrão `<project>.supabase.co` →
 * `<project>.supabase.co/functions/v1` é mecânico e sempre correto na
 * configuração default de qualquer projeto Supabase.
 */
function resolveFunctionsBaseUrl(): string {
  const explicit = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL as string | undefined;
  if (explicit) return explicit.replace(/\/$/, '');

  const projectUrl = requireEnv('VITE_SUPABASE_URL').replace(/\/$/, '');
  return `${projectUrl}/functions/v1/api`;
}

/** Monta a URL completa de uma rota da Edge Function `api`.
 *  Ex: buildFunctionUrl('/firms') → 'https://xxx.supabase.co/functions/v1/api/firms' */
function buildFunctionUrl(path: string): string {
  const functionsUrl = resolveFunctionsBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${functionsUrl}${normalizedPath}`;
}

/**
 * Substitui todo `fetch('/api/...')` direto usado no código anterior
 * (Firebase). Mesma assinatura de uso que `fetch()` (aceita RequestInit
 * opcional), mas resolve a URL absoluta e injeta o header `apikey`
 * automaticamente — nenhuma fonte precisa saber desses dois detalhes.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const anonKey = requireEnv('VITE_SUPABASE_ANON_KEY');
  const url = buildFunctionUrl(path);

  return fetch(url, {
    ...init,
    headers: {
      ...init?.headers,
      apikey: anonKey,
    },
  });
}
